import { Link, useLocation } from "wouter";
import { LumaLogo } from "./LumaLogo";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { trackClick } from "@/lib/tracking";
import { ConsentBanner } from "./ConsentBanner";

export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/events", label: "Events" },
    { href: "/partnerships", label: "Partners" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* 3px gold accent bar at very top */}
      <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-primary to-transparent flex-shrink-0" />

      <header className="sticky top-0 z-50 w-full bg-background/90 backdrop-blur-md border-b border-white/5">
        <div className="container flex h-16 items-center justify-between mx-auto px-6">
          <Link href="/" className="flex items-center gap-2">
            <LumaLogo variant="wordmark" className="h-11 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-xs font-medium uppercase tracking-[0.2em]">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`transition-colors hover:text-primary pb-0.5 border-b border-transparent hover:border-primary/60 ${
                  location === link.href
                    ? "text-primary border-b border-primary"
                    : "text-muted-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link href="/contact">
              <Button
                size="sm"
                onClick={() =>
                  trackClick({
                    targetType: "cta",
                    targetSlug: "header-work-with-us",
                    targetName: "Work With Us",
                    pagePath: location,
                  })
                }
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading tracking-widest text-xs h-8 px-5 rounded-full"
              >
                Work With Us
              </Button>
            </Link>
            <Link href="/admin/login">
              <button className="text-muted-foreground/70 hover:text-muted-foreground transition-colors text-[10px] font-heading tracking-widest uppercase">
                Login
              </button>
            </Link>
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              className="text-foreground p-2"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-background">
            <nav className="flex flex-col p-6 space-y-5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-heading text-2xl uppercase tracking-widest ${
                    location === link.href ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href="/contact"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  trackClick({
                    targetType: "cta",
                    targetSlug: "header-work-with-us",
                    targetName: "Work With Us",
                    pagePath: location,
                  });
                }}
              >
                <Button className="w-full bg-primary text-primary-foreground font-heading tracking-widest uppercase mt-2 rounded-full">
                  Work With Us
                </Button>
              </Link>
              <Link
                href="/admin/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-muted-foreground/70 hover:text-muted-foreground transition-colors text-xs font-heading tracking-widest uppercase text-center pt-2"
                data-testid="mobile-menu-login"
              >
                Login
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 w-full relative">
        {children}
      </main>

      <footer className="bg-background py-14">
        <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
          <LumaLogo variant="wordmark" className="h-14 w-auto" />
          <div className="flex flex-col items-center md:items-end gap-2">
            <p className="font-heading text-secondary tracking-widest text-sm">
              LIGHT IT UP.
            </p>
            <p className="font-serif italic text-muted-foreground text-sm">
              You were always the show. We just turned on the lights.
            </p>
            <p className="text-xs text-muted-foreground/80 mt-1">
              &copy; {new Date().getFullYear()} LUMA Entertainment. All rights reserved.
            </p>
            <Link
              href="/privacy"
              className="text-xs text-muted-foreground/80 hover:text-secondary transition-colors mt-1"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>

      <ConsentBanner />
    </div>
  );
}
